import React, { useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { TextField, Button, Alert, Box, Typography, Link } from '@mui/material';
import AuthCard from '../../components/AuthCard';
import { useAuth } from '../../auth/AuthContext';
import { formatAuthError } from '../../auth/formatAuthError';

export default function ConfirmPage() {
  const { confirmEmail, resendCode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();

  // The confirmation email links here as /confirm?email=…&code=… (built by the
  // Cognito custom-message Lambda). Read both from the query string, falling
  // back to the email handed over in router state when arriving in-app from the
  // Register page. Without the email there is no username for confirmSignUp.
  const email = params.get('email') || location.state?.email || '';
  const [code, setCode] = useState(() => params.get('code') || '');
  const [error, setError] = useState('');
  const [resent, setResent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email) {
      setError('We couldn’t tell which account to confirm. Open the link in your confirmation email, or register again.');
      return;
    }
    if (!code.trim()) {
      setError('Enter the confirmation code from your email.');
      return;
    }
    setSubmitting(true);
    try {
      await confirmEmail(email, code.trim());
      navigate('/login');
    } catch (err) {
      setError(formatAuthError(err.message) || 'Invalid code.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setError('');
    if (!email) {
      setError('We need your email to resend the code — please register again.');
      return;
    }
    await resendCode(email);
    setResent(true);
  };

  return (
    <AuthCard
      title="Check your email"
      subtitle={email ? `We sent a confirmation code to ${email}` : 'Enter the confirmation code from your email'}
    >
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {resent && <Alert severity="success" sx={{ mb: 2 }}>Code resent.</Alert>}
      <Box component="form" onSubmit={onSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField label="Confirmation code" size="small" fullWidth
          value={code} onChange={(e) => setCode(e.target.value)} />
        <Button type="submit" variant="contained" fullWidth disabled={submitting}
          sx={{ bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' }, textTransform: 'none', fontWeight: 600 }}>
          {submitting ? 'Confirming…' : 'Confirm'}
        </Button>
      </Box>
      <Typography variant="body2" textAlign="center" mt={2} color="text.secondary">
        Didn't receive it?{' '}
        <Link component="button" onClick={handleResend} sx={{ color: '#3B82F6', fontWeight: 500 }}>
          Resend code
        </Link>
      </Typography>
    </AuthCard>
  );
}
